import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppNav } from '../../components/layout/AppNav';
import { Badge } from '../../components/ui/Badge';
import { offersService, type Offer } from '../../services/offers.service';
import { productsService } from '../../services/products.service';
import { useAuth } from '../../hooks/useAuth';
import { formatSAR, formatDate } from '../../utils/formatters';
import {
  LayoutDashboard, CheckCircle2, Send, FileText, XCircle, Clock, Archive, X, Plus, TrendingUp,
} from 'lucide-react';

interface Tile {
  label: string;
  value: number;
  filter: string | null;
  icon: React.ReactNode;
  accent: string;
}

function isExpired(offer: Offer) {
  if (offer.status === 'Expired') return true;
  if (offer.status === 'Sent' && offer.customerLinkExpiresAt) {
    return new Date(offer.customerLinkExpiresAt).getTime() < Date.now();
  }
  return false;
}

function linkExpiryDisplay(expiresAt: string | undefined): { text: string; color: string } | null {
  if (!expiresAt) return null;
  const ms = new Date(expiresAt).getTime() - Date.now();
  const days = ms / (1000 * 60 * 60 * 24);
  const text = formatDate(expiresAt);
  if (days < 1) return { text, color: 'var(--err)' };
  if (days < 5) return { text, color: 'var(--warn)' };
  return { text, color: 'var(--text-2)' };
}

export const DashboardPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [allOffers, setAllOffers] = useState<Offer[]>([]);
  const [productMap, setProductMap] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([
      offersService.list().catch(() => []),
      productsService.list().catch(() => []),
    ]).then(([offers, products]) => {
      setAllOffers(offers);
      const map: Record<string, string> = {};
      products.forEach((p) => { map[p.id] = p.name; });
      setProductMap(map);
    }).finally(() => setLoading(false));
  }, []);

  const stats = {
    total:    allOffers.length,
    accepted: allOffers.filter((o) => o.status === 'Accepted').length,
    sent:     allOffers.filter((o) => o.status === 'Sent' && !isExpired(o)).length,
    draft:    allOffers.filter((o) => o.status === 'Draft').length,
    rejected: allOffers.filter((o) => o.status === 'Rejected').length,
    closed:   allOffers.filter((o) => o.status === 'Closed').length,
    expired:  allOffers.filter((o) => isExpired(o)).length,
  };

  const tiles: Tile[] = [
    { label: 'Total Offers',  value: stats.total,    filter: null,        icon: <LayoutDashboard size={20} />, accent: 'var(--p)' },
    { label: 'Accepted',      value: stats.accepted, filter: 'Accepted',  icon: <CheckCircle2 size={20} />,    accent: 'var(--succ)' },
    { label: 'Sent',          value: stats.sent,     filter: 'Sent',      icon: <Send size={20} />,            accent: '#3B82F6' },
    { label: 'Drafts',        value: stats.draft,    filter: 'Draft',     icon: <FileText size={20} />,        accent: '#9CA3AF' },
    { label: 'Rejected',      value: stats.rejected, filter: 'Rejected',  icon: <XCircle size={20} />,         accent: 'var(--err)' },
    { label: 'Expired',       value: stats.expired,  filter: 'Expired',   icon: <Clock size={20} />,           accent: '#F59E0B' },
    { label: 'Closed',        value: stats.closed,   filter: 'Closed',    icon: <Archive size={20} />,         accent: '#6366F1' },
  ];

  const filteredOffers = (() => {
    if (!activeFilter) return allOffers.slice(0, 10);
    if (activeFilter === 'Expired') return allOffers.filter(isExpired);
    return allOffers.filter((o) => o.status === activeFilter);
  })();

  const hasLinkExpiry = filteredOffers.some((o) => o.status === 'Sent' && o.customerLinkExpiresAt);

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <AppNav
        title={`Welcome, ${user?.name ?? 'Agent'}`}
        subtitle="Here's a summary of your recent activity."
      />
      <main style={{ flex: 1, padding: '24px', overflowY: 'auto' }}>

        {/* Page header */}
        <div className="page-header">
          <div>
            <div className="page-title">Dashboard</div>
            <div style={{ fontSize: '13px', color: 'var(--text-2)', marginTop: '2px' }}>
              {loading ? 'Loading…' : `${stats.total} offer${stats.total !== 1 ? 's' : ''} total`}
            </div>
          </div>
          <button
            className="btn-primary"
            onClick={() => navigate('/offers/wizard/location')}
            style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
          >
            <Plus size={15} />
            New Offer
          </button>
        </div>

        {/* Metric tiles */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px', marginBottom: '24px' }}>
          {tiles.map((tile) => {
            const isActive = activeFilter === tile.filter;
            return (
              <div
                key={tile.label}
                className="metric-tile"
                onClick={() => setActiveFilter((prev) => prev === tile.filter ? null : tile.filter)}
                style={{
                  border: isActive ? `2px solid ${tile.accent}` : '1px solid var(--border)',
                  background: isActive ? `${tile.accent}10` : 'var(--surface)',
                  cursor: 'pointer',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                  <div style={{ color: tile.accent, opacity: isActive ? 1 : 0.7 }}>{tile.icon}</div>
                  {isActive && (
                    <button
                      onClick={(e) => { e.stopPropagation(); setActiveFilter(null); }}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-3)', display: 'flex', padding: '0' }}
                    >
                      <X size={12} />
                    </button>
                  )}
                </div>
                <div style={{ fontSize: '26px', fontWeight: 800, color: isActive ? tile.accent : 'var(--text-1)', lineHeight: 1 }}>
                  {loading ? <div className="skeleton" style={{ width: '40px', height: '26px' }} /> : tile.value}
                </div>
                <div style={{ fontSize: '12.5px', color: 'var(--text-2)', marginTop: '4px', fontWeight: 500 }}>
                  {tile.label}
                </div>
                <div className="metric-tile-accent" style={{ background: tile.accent }} />
              </div>
            );
          })}
        </div>

        {/* Offers table */}
        <div className="card">
          <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span className="section-title" style={{ marginBottom: 0 }}>
                {activeFilter ? `${activeFilter} Offers` : 'Recent Offers'}
              </span>
              {!activeFilter && allOffers.length > 10 && (
                <span style={{ fontSize: '12px', color: 'var(--text-3)' }}>({allOffers.length} total)</span>
              )}
            </div>
            <button
              className="btn-ghost btn-sm"
              onClick={() => navigate('/my-offers')}
            >
              View All
            </button>
          </div>

          {loading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-3)' }}>
              <div className="skeleton" style={{ height: '14px', width: '200px', margin: '0 auto' }} />
            </div>
          ) : filteredOffers.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon"><FileText size={24} /></div>
              <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-1)', marginBottom: '4px' }}>
                {activeFilter ? `No ${activeFilter} offers` : 'No offers yet'}
              </div>
              <div style={{ fontSize: '13px', color: 'var(--text-2)' }}>
                {activeFilter ? 'Try a different filter.' : 'Start by creating your first offer.'}
              </div>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="tbl">
                <thead>
                  <tr>
                    {['Offer #', 'Customer', 'Status', 'Total', 'Created By', 'Created', ...(hasLinkExpiry ? ['Link Expires'] : [])].map((h) => (
                      <th key={h}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredOffers.map((offer) => {
                    const expiry = offer.status === 'Sent' ? linkExpiryDisplay(offer.customerLinkExpiresAt) : null;
                    return (
                      <tr
                        key={offer.id}
                        onClick={() => navigate(`/my-offers/${offer.id}`)}
                        style={{ cursor: 'pointer' }}
                      >
                        <td style={{ fontWeight: 700, color: 'var(--p)' }}>{offer.offerNumber}</td>
                        <td>{offer.customer?.nameEn ?? <span style={{ color: 'var(--text-3)' }}>—</span>}</td>
                        <td><Badge label={offer.status} /></td>
                        <td style={{ fontWeight: 600 }}>{formatSAR(offer.total)}</td>
                        <td style={{ color: 'var(--text-2)', fontSize: '13px' }}>
                          {offer.agentName ?? <span style={{ color: 'var(--text-3)' }}>—</span>}
                        </td>
                        <td style={{ color: 'var(--text-2)', fontSize: '13px', whiteSpace: 'nowrap' }}>
                          {formatDate(offer.createdAt)}
                        </td>
                        {hasLinkExpiry && (
                          <td style={{ fontSize: '13px', whiteSpace: 'nowrap' }}>
                            {expiry ? (
                              <span style={{ color: expiry.color, fontWeight: 600 }}>{expiry.text}</span>
                            ) : (
                              <span style={{ color: 'var(--text-3)' }}>—</span>
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Bottom row — Top Products + Conversion Summary */}
        {!loading && allOffers.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginTop: '16px' }}>

            {/* Top Products */}
            <div className="card">
              <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <TrendingUp size={15} color="var(--p)" />
                <span className="section-title" style={{ marginBottom: 0 }}>Top Products (Accepted)</span>
              </div>
              <div className="card-body" style={{ padding: '16px 20px' }}>
                {(() => {
                  const counts: Record<string, { name: string; count: number; value: number }> = {};
                  allOffers.filter((o) => o.status === 'Accepted').forEach((o) => {
                    (o.items ?? []).forEach((item) => {
                      const name = productMap[item.productId] ?? item.product?.name ?? item.productId ?? 'Unknown';
                      if (!counts[name]) counts[name] = { name, count: 0, value: 0 };
                      counts[name].count++;
                      counts[name].value += Number(item.price) || 0;
                    });
                  });
                  const sorted = Object.values(counts).sort((a, b) => b.value - a.value).slice(0, 5);
                  const maxVal = sorted[0]?.value || 1;
                  if (sorted.length === 0) return (
                    <div style={{ color: 'var(--text-3)', fontSize: 13, textAlign: 'center', padding: '16px 0' }}>No product data yet.</div>
                  );
                  return sorted.map((p, i) => (
                    <div key={p.name} style={{ marginBottom: i < sorted.length - 1 ? 16 : 0 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                        <span style={{ fontSize: 13.5, fontWeight: 500, color: 'var(--text-1)' }}>{p.name}</span>
                        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-2)' }}>{formatSAR(p.value)}</span>
                      </div>
                      <div style={{ height: 5, borderRadius: 3, background: 'var(--border)' }}>
                        <div style={{ height: '100%', borderRadius: 3, background: 'var(--p)', width: `${(p.value / maxVal) * 100}%`, transition: 'width .5s ease' }} />
                      </div>
                      <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginTop: 3 }}>{p.count} offer{p.count !== 1 ? 's' : ''}</div>
                    </div>
                  ));
                })()}
              </div>
            </div>

            {/* Conversion Summary — 2×2 grid */}
            <div className="card">
              <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <CheckCircle2 size={15} color="var(--succ)" />
                <span className="section-title" style={{ marginBottom: 0 }}>Conversion Summary</span>
              </div>
              <div style={{ padding: '16px' }}>
                {(() => {
                  const sentOffers = allOffers.filter((o) => ['Sent', 'Accepted', 'Rejected', 'Closed', 'Expired'].includes(o.status));
                  const accepted = allOffers.filter((o) => o.status === 'Accepted');
                  const active = allOffers.filter((o) => o.status === 'Sent');
                  const convRate = sentOffers.length > 0 ? Math.round((accepted.length / sentOffers.length) * 100) : 0;
                  const monthlyRev = accepted.reduce((s, o) => s + (Number(o.total) || 0), 0);
                  // Avg time: accepted offers, use (updatedAt - sentAt) as proxy
                  const avgDays = (() => {
                    const times = accepted
                      .filter((o) => o.sentAt && o.updatedAt)
                      .map((o) => (new Date(o.updatedAt).getTime() - new Date(o.sentAt!).getTime()) / 86400000);
                    return times.length ? (times.reduce((a, b) => a + b, 0) / times.length).toFixed(1) : '—';
                  })();
                  const boxes = [
                    { label: 'CONVERSION RATE', value: `${convRate}%`, sub: 'Sent → Accepted', color: convRate >= 50 ? '#3B82F6' : convRate >= 25 ? 'var(--warn)' : 'var(--err)' },
                    { label: 'MONTHLY REVENUE', value: formatSAR(monthlyRev), sub: 'Accepted offers', color: 'var(--p)' },
                    { label: 'AVG. TIME TO ACCEPT', value: typeof avgDays === 'string' && avgDays !== '—' ? `${avgDays}d` : avgDays, sub: 'Days from sent', color: 'var(--text-1)' },
                    { label: 'ACTIVE OFFERS', value: String(active.length), sub: 'Awaiting response', color: '#3B82F6' },
                  ];
                  return (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                      {boxes.map((b) => (
                        <div key={b.label} style={{ background: 'var(--surface-alt)', borderRadius: 10, padding: '14px 16px' }}>
                          <div style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>{b.label}</div>
                          <div style={{ fontSize: 24, fontWeight: 800, color: b.color, lineHeight: 1 }}>{b.value}</div>
                          <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 4 }}>{b.sub}</div>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};
